/**
 * V13.21 (Itération C) — Annuaire public "Athlètes du Pacifique"
 *
 * Route : /annuaire/athletes-du-pacifique
 *
 * Population documentaire (délégations ≠ Polynésie française), issue des
 * imports historiques des Jeux et Mini-Jeux du Pacifique.
 * Séparée de l'annuaire principal /athletes (AITO PF).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Globe, Search, Flag, Trophy, Medal, Filter, ChevronRight, Users, MapPin, Info,
} from "lucide-react";

const ALL = "__all__";
const SEX_LABEL = { homme: "Homme", femme: "Femme", autre: "Autre" };

function formatDiscipline(slug) {
  if (!slug) return "—";
  return String(slug)
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function MedalPill({ label, value, tone }) {
  const cls = {
    or: "text-[hsl(var(--aito-gold))]",
    argent: "text-slate-500 dark:text-slate-300",
    bronze: "text-amber-700 dark:text-amber-500",
  }[tone] || "text-foreground";
  if (!value) return null;
  return (
    <span className={`inline-flex items-baseline gap-0.5 text-xs ${cls}`}>
      <span className="font-mono font-bold">{value}</span>
      <span className="opacity-70">{label}</span>
    </span>
  );
}

function PacificAthleteCard({ pa }) {
  const s = pa.stats || {};
  const initial = (pa.display_name || "?").charAt(0).toUpperCase();
  return (
    <Link
      to={`/pacific-athletes/${pa.id}`}
      data-testid={`pacific-athlete-card-${pa.id}`}
      className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--aito-lagoon))] rounded-xl"
    >
      <Card className="p-4 h-full border-slate-200 hover:border-[hsl(var(--aito-lagoon))]/40 hover:shadow-md transition-[box-shadow,border-color,transform] duration-200 group-hover:-translate-y-0.5">
        <div className="flex items-start gap-3">
          <div className="shrink-0 h-12 w-12 rounded-full bg-[hsl(var(--aito-lagoon))]/10 border border-[hsl(var(--aito-lagoon))]/25 flex items-center justify-center overflow-hidden">
            {pa.photo_url ? (
              <img src={pa.photo_url} alt={pa.display_name} className="h-full w-full object-cover" />
            ) : (
              <span className="font-display text-lg font-bold text-[hsl(var(--aito-lagoon))]">{initial}</span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-display font-bold text-sm sm:text-base leading-tight truncate">
              {pa.display_name}
            </h3>
            <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
              <Flag className="h-3 w-3 text-[hsl(var(--aito-gold))]" />
              <span className="truncate">{pa.delegation_name || pa.delegation_code || "—"}</span>
            </div>
            {(pa.disciplines || []).length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {pa.disciplines.slice(0, 2).map((d) => (
                  <Badge key={d} variant="outline" className="text-[10px] font-normal">
                    {formatDiscipline(d)}
                  </Badge>
                ))}
                {pa.disciplines.length > 2 && (
                  <span className="text-[10px] text-muted-foreground">+{pa.disciplines.length - 2}</span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="mt-3 pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs">
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <Trophy className="h-3 w-3" />
            {s.participations || 0} participation{(s.participations || 0) > 1 ? "s" : ""}
          </span>
          <div className="flex items-center gap-2">
            <MedalPill label="🥇" value={s.or} tone="or" />
            <MedalPill label="🥈" value={s.argent} tone="argent" />
            <MedalPill label="🥉" value={s.bronze} tone="bronze" />
            {!s.total && <span className="italic opacity-60">Aucun podium</span>}
          </div>
        </div>
      </Card>
    </Link>
  );
}

function CardSkeleton() {
  return (
    <Card className="p-4">
      <div className="flex items-start gap-3">
        <Skeleton className="h-12 w-12 rounded-full shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
      <Skeleton className="h-3 w-full mt-4" />
    </Card>
  );
}

export default function PacificAthletesPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [facets, setFacets] = useState({ delegations: [], disciplines: [], editions: [] });

  const [q, setQ] = useState("");
  const [delegation, setDelegation] = useState(ALL);
  const [discipline, setDiscipline] = useState(ALL);
  const [editionId, setEditionId] = useState(ALL);
  const [medal, setMedal] = useState(ALL);
  const [sex, setSex] = useState(ALL);

  useEffect(() => {
    api
      .get("/pacific-athletes/facets/list")
      .then((r) => setFacets(r.data || {}))
      .catch(() => setFacets({ delegations: [], disciplines: [], editions: [] }));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (delegation !== ALL) params.delegation = delegation;
    if (discipline !== ALL) params.discipline = discipline;
    if (editionId !== ALL) params.edition_id = editionId;
    if (medal !== ALL) params.medal = medal;
    if (sex !== ALL) params.sex = sex;
    const t = setTimeout(() => {
      api
        .get("/pacific-athletes", { params })
        .then((r) => {
          if (cancelled) return;
          setItems(r.data?.items || []);
          setTotal(r.data?.count || 0);
        })
        .catch(() => {
          if (cancelled) return;
          setItems([]);
          setTotal(0);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250); // debounce recherche
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, delegation, discipline, editionId, medal, sex]);

  const hasActiveFilter = useMemo(
    () => q || delegation !== ALL || discipline !== ALL || editionId !== ALL || medal !== ALL || sex !== ALL,
    [q, delegation, discipline, editionId, medal, sex]
  );

  const clearFilters = () => {
    setQ(""); setDelegation(ALL); setDiscipline(ALL);
    setEditionId(ALL); setMedal(ALL); setSex(ALL);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6" data-testid="pacific-athletes-page">
      {/* Hero */}
      <div className="rounded-2xl aito-grad-hero text-white p-5 sm:p-7 mb-5 overflow-hidden">
        <div className="flex items-start gap-4">
          <div className="rounded-full bg-white/15 border border-white/25 h-11 w-11 flex items-center justify-center shrink-0">
            <Globe className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="font-display font-bold text-xl sm:text-2xl leading-tight">
              Athlètes du Pacifique
            </h1>
            <p className="mt-1 text-sm text-white/85 max-w-2xl leading-relaxed">
              Retrouvez les sportifs des délégations du Pacifique référencés à partir des
              résultats historiques des Jeux et Mini-Jeux du Pacifique.
              Cette base est <strong>documentaire</strong> — elle ne se substitue pas à
              l'annuaire principal des athlètes polynésiens.
            </p>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <Card className="p-3 mb-4" data-testid="pacific-athletes-filters">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground mr-1">
            <Filter className="h-3.5 w-3.5" />
            Filtres
          </div>

          <div className="relative min-w-[220px] flex-1 max-w-md">
            <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Nom, prénom, alias…"
              className="pl-8 h-9"
              data-testid="pacific-filter-search"
            />
          </div>

          <Select value={delegation} onValueChange={setDelegation}>
            <SelectTrigger className="h-9 w-[160px]" data-testid="pacific-filter-delegation">
              <SelectValue placeholder="Délégation" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Toutes délégations</SelectItem>
              {(facets.delegations || []).map((d) => (
                <SelectItem key={d} value={d}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={discipline} onValueChange={setDiscipline}>
            <SelectTrigger className="h-9 w-[160px]" data-testid="pacific-filter-discipline">
              <SelectValue placeholder="Discipline" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Toutes disciplines</SelectItem>
              {(facets.disciplines || []).map((d) => (
                <SelectItem key={d} value={d}>{formatDiscipline(d)}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={editionId} onValueChange={setEditionId}>
            <SelectTrigger className="h-9 w-[160px]" data-testid="pacific-filter-edition">
              <SelectValue placeholder="Édition" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Toutes éditions</SelectItem>
              {(facets.editions || []).map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.name} ({e.year})</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={medal} onValueChange={setMedal}>
            <SelectTrigger className="h-9 w-[130px]" data-testid="pacific-filter-medal">
              <SelectValue placeholder="Médaille" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Toutes médailles</SelectItem>
              <SelectItem value="any">🏅 Toute médaille</SelectItem>
              <SelectItem value="or">🥇 Or</SelectItem>
              <SelectItem value="argent">🥈 Argent</SelectItem>
              <SelectItem value="bronze">🥉 Bronze</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sex} onValueChange={setSex}>
            <SelectTrigger className="h-9 w-[110px]" data-testid="pacific-filter-sex">
              <SelectValue placeholder="Sexe" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous</SelectItem>
              <SelectItem value="homme">Homme</SelectItem>
              <SelectItem value="femme">Femme</SelectItem>
              <SelectItem value="autre">Autre</SelectItem>
            </SelectContent>
          </Select>

          {hasActiveFilter && (
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="pacific-filter-clear">
              Effacer
            </Button>
          )}
        </div>
        <div className="mt-2 text-xs text-muted-foreground" data-testid="pacific-athletes-count">
          {loading
            ? "Chargement…"
            : `${total.toLocaleString("fr-FR")} athlète${total > 1 ? "s" : ""} du Pacifique${hasActiveFilter ? " (filtré)" : ""}`}
        </div>
      </Card>

      {/* Grille */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <CardSkeleton key={i} />)}
        </div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center border-dashed" data-testid="pacific-athletes-empty">
          <Users className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
          <h3 className="font-display font-semibold text-lg mb-1">
            {hasActiveFilter ? "Aucun résultat pour ce filtre" : "Aucun athlète référencé pour l'instant"}
          </h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {hasActiveFilter
              ? "Essayez d'élargir la recherche ou d'effacer les filtres."
              : "Cette base s'enrichit au fil des imports des Jeux et Mini-Jeux du Pacifique."}
          </p>
          {hasActiveFilter && (
            <Button variant="outline" onClick={clearFilters} className="mt-4" data-testid="pacific-athletes-empty-clear">
              Effacer les filtres
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="pacific-athletes-grid">
          {items.map((pa) => <PacificAthleteCard key={pa.id} pa={pa} />)}
        </div>
      )}

      <p className="mt-6 text-[11px] italic text-muted-foreground text-center flex items-center justify-center gap-1.5">
        <Info className="h-3 w-3" />
        Données extraites des archives publiques des Jeux du Pacifique. Toute correction peut être signalée à l'équipe éditoriale.
      </p>
    </div>
  );
}
